// import { useEffect, useRef, useCallback } from 'react';

// /**
//  * Primes an AudioContext on the first user gesture (click, keydown, touchstart)
//  * so that subsequent programmatic audio calls aren't blocked by the browser's
//  * autoplay policy.
//  *
//  * Returns a stable `playNewOrderSound` callback that is safe to call from
//  * socket event handlers (no user gesture required after the first unlock).
//  */
// const useSoundUnlock = () => {
//     const ctxRef = useRef(null);
//     const unlockedRef = useRef(false);

//     // ── Create (but don't start) the shared AudioContext ──────────────────────
//     const getCtx = useCallback(() => {
//         if (!ctxRef.current) {
//             ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
//         }
//         return ctxRef.current;
//     }, []);

//     // ── Unlock on the very first user gesture ──────────────────────────────────
//     useEffect(() => {
//         const unlock = async () => {
//             if (unlockedRef.current) return;

//             try {
//                 const ctx = getCtx();

//                 // A zero-length silent buffer is enough to satisfy the browser
//                 const buffer = ctx.createBuffer(1, 1, 22050);
//                 const source = ctx.createBufferSource();
//                 source.buffer = buffer;
//                 source.connect(ctx.destination);
//                 source.start(0);

//                 await ctx.resume();
//                 unlockedRef.current = true;
//             } catch (e) {
//                 // Non-fatal — will retry on the next gesture
//             }
//         };

//         const events = ['click', 'keydown', 'touchstart', 'pointerdown'];
//         events.forEach(e => window.addEventListener(e, unlock, { once: false, passive: true }));

//         return () => events.forEach(e => window.removeEventListener(e, unlock));
//     }, [getCtx]);

//     // ── Bell tone helper (pure function, uses the shared ctx) ──────────────────
//     const bell = useCallback((ctx, fundamental, startTime, duration, gain = 0.6) => {
//         const connect = (freq, gainVal, decayEnd) => {
//             const osc = ctx.createOscillator();
//             const gainNode = ctx.createGain();
//             osc.type = 'sine';
//             osc.frequency.setValueAtTime(freq, startTime);
//             gainNode.gain.setValueAtTime(0, startTime);
//             gainNode.gain.linearRampToValueAtTime(gainVal, startTime + 0.004);
//             gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + decayEnd);
//             osc.connect(gainNode);
//             gainNode.connect(ctx.destination);
//             osc.start(startTime);
//             osc.stop(startTime + duration);
//         };

//         connect(fundamental, gain, duration);
//         connect(fundamental * 2.756, gain * 0.5, duration * 0.6);
//         connect(fundamental * 5.404, gain * 0.25, duration * 0.3);
//     }, []);

//     // ── Main sound function ────────────────────────────────────────────────────
//     const playNewOrderSound = useCallback(async () => {
//         try {
//             const ctx = getCtx();

//             // If still suspended (gesture not yet received), try resuming anyway.
//             // It may fail silently — that's acceptable.
//             if (ctx.state === 'suspended') {
//                 await ctx.resume();
//             }

//             // If the browser still won't let us play, bail out gracefully
//             if (ctx.state !== 'running') return;

//             const now = ctx.currentTime;
//             bell(ctx, 1400, now, 0.9, 0.55);
//             bell(ctx, 1050, now + 0.22, 1.1, 0.50);

//             // WAV notification after chime
//             setTimeout(() => {
//                 const audio = new Audio('/order-received.wav');
//                 audio.volume = 1;
//                 audio.play().catch(() => { });   // swallow — wav is optional
//             }, 600);

//         } catch (err) {
//             console.warn('Could not play new order sound:', err);
//         }
//     }, [getCtx, bell]);

//     return { playNewOrderSound };
// };

// export default useSoundUnlock;











import { useEffect, useRef, useCallback } from 'react';

const VOICE_STORAGE_KEY = 'kds-tts-voice';

/**
 * Primes an AudioContext on the first user gesture and exposes:
 *   - playNewOrderSound(items?)            — bell chime + spoken order summary
 *   - speakOrderSummary(items?, options?)  — spoken order summary only
 *
 * The TTS voice is read from localStorage (kds-tts-voice = voice.name).
 * KitchenLayout's VoiceSettings panel writes that key, so changes take effect
 * on the very next sound without needing a page reload.
 */
const useSoundUnlock = () => {
    const ctxRef = useRef(null);
    const unlockedRef = useRef(false);
    const voicesRef = useRef([]);   // full list, kept current

    // ── Keep the voices list current ───────────────────────────────────────────
    useEffect(() => {
        const sync = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
        sync();
        window.speechSynthesis.addEventListener('voiceschanged', sync);
        return () => window.speechSynthesis.removeEventListener('voiceschanged', sync);
    }, []);

    // ── Pick the voice to use right now ────────────────────────────────────────
    const resolveVoice = useCallback(() => {
        const saved = localStorage.getItem(VOICE_STORAGE_KEY);
        const voices = voicesRef.current;

        if (saved) {
            const match = voices.find(v => v.name === saved);
            if (match) return match;
        }

        return (
            voices.find(v => v.name.includes('Google UK English Female')) ||
            voices.find(v => v.name.includes('Microsoft Zira')) ||
            voices.find(v => v.name.includes('Samantha')) ||
            voices.find(v => v.lang.startsWith('en')) ||
            null
        );
    }, []);

    // ── Shared AudioContext ────────────────────────────────────────────────────
    const getCtx = useCallback(() => {
        if (!ctxRef.current) {
            ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return ctxRef.current;
    }, []);

    // ── Unlock on first user gesture ───────────────────────────────────────────
    useEffect(() => {
        const unlock = async () => {
            if (unlockedRef.current) return;
            try {
                const ctx = getCtx();
                const buf = ctx.createBuffer(1, 1, 22050);
                const src = ctx.createBufferSource();
                src.buffer = buf;
                src.connect(ctx.destination);
                src.start(0);
                await ctx.resume();
                unlockedRef.current = true;
            } catch (_) { /* retry on next gesture */ }
        };

        const events = ['click', 'keydown', 'touchstart', 'pointerdown'];
        events.forEach(e => window.addEventListener(e, unlock, { once: false, passive: true }));
        return () => events.forEach(e => window.removeEventListener(e, unlock));
    }, [getCtx]);

    // ── Bell tone ──────────────────────────────────────────────────────────────
    const bell = useCallback((ctx, fundamental, startTime, duration, gain = 0.6) => {
        const partial = (freq, gainVal, decayEnd) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, startTime);
            g.gain.setValueAtTime(0, startTime);
            g.gain.linearRampToValueAtTime(gainVal, startTime + 0.004);
            g.gain.exponentialRampToValueAtTime(0.001, startTime + decayEnd);
            osc.connect(g);
            g.connect(ctx.destination);
            osc.start(startTime);
            osc.stop(startTime + duration);
        };
        partial(fundamental, gain, duration);
        partial(fundamental * 2.756, gain * 0.5, duration * 0.6);
        partial(fundamental * 5.404, gain * 0.25, duration * 0.3);
    }, []);

    // ── Speak order summary ────────────────────────────────────────────────────
    // Shared by playNewOrderSound (auto-announce on a new ticket) and the manual
    // "speak" button on a Card/DenseCard — pass { announce: false } there to skip
    // the "Order received" preamble since the user already knows what it is.
    // useSoundUnlock.js

    const speakOrderSummary = useCallback((items, { announce = true, tableNum } = {}) => {
        window.speechSynthesis.cancel();

        const lines = (items ?? [])
            .filter(item => item?.ItemName)
            .map(item => {
                const qty = Number(item.Quantity) || 1;
                const unit = qty === 1 ? 'order' : 'orders';
                const base = `${qty} ${unit} of ${item.ItemName}`;
                const modification = item.Modifications;
                const hasModification = modification && modification.trim() !== '';
                return hasModification ? `${base}, ${modification}` : base;
            });

        const tableLabel = tableNum
            ? tableNum === 'Take-Away'
                ? 'Take Away'
                : `Table ${tableNum}`
            : null;

        const preamble = announce
            ? tableLabel
                ? `New order for ${tableLabel}`
                : 'Order received'
            : tableLabel
                ? `Order for ${tableLabel}`
                : null;

        const parts = [preamble, ...lines].filter(Boolean);
        const fullText = parts.join('. ');

        const utterance = new SpeechSynthesisUtterance(fullText);
        utterance.voice = resolveVoice();
        utterance.rate = 0.88;
        utterance.pitch = 1.0;
        utterance.volume = 1;
        window.speechSynthesis.speak(utterance);
    }, [resolveVoice]);

    const playNewOrderSound = useCallback(async (items, tableNum) => {
        try {
            const ctx = getCtx();
            if (ctx.state === 'suspended') await ctx.resume();
            if (ctx.state !== 'running') return;

            const now = ctx.currentTime;
            bell(ctx, 1400, now, 0.9, 0.55);
            bell(ctx, 1050, now + 0.22, 1.1, 0.50);

            setTimeout(() => {
                speakOrderSummary(items, { announce: true, tableNum });
            }, 500);
        } catch (err) {
            console.warn('Could not play new order sound:', err);
        }
    }, [getCtx, bell, speakOrderSummary]);

    return { playNewOrderSound, speakOrderSummary };
};

export default useSoundUnlock;